import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, Tab, Box, Button, styled } from '@material-ui/core';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Content, Header, Progress } from '@backstage/core-components';
import ArrowBack from '@material-ui/icons/ArrowBack';
import { EditUserDrawer } from './components/drawers/EditUserDrawer';
import { CreateUserDrawer } from './components/drawers/CreateUserDrawer';
import { CreateOrgDrawer } from './components/drawers/CreateOrgDrawer';
import { useIdentity } from '@internal/shared';
import { EditOrgDrawer } from './components/drawers/EditOrgDrawer';
import { Confirm } from './components/Confirm';
import {
  InviteUserModal,
  InviteApplication,
} from './components/InviteUserModal';
import { useUser } from './hooks/useUsers';
import {
  USER_MANAGEMENT_READ,
  USER_MANAGEMENT_ORG_ADMIN_ACCESS,
} from './constants';
import { useUserManagementContext } from './context/UserManagementContext';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { FlexBox } from '../global/FlexBox';
import AddIcon from '@material-ui/icons/Add';
import { useUserManagementPermissions } from './hooks';
import { PageTitle } from '../../shared/components';

const routes = ['users', 'teams'];

const StyledTab = styled(Tab)({
  fontWeight: 700,
  minWidth: 0,
  padding: '12px 16px',
  fontFamily: 'Red Hat Display',
  ['&:not(&.Mui-selected)']: {
    color: 'rgba(9, 34, 65, 0.7)',
  },
});

const UserManagementLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDetailsPage = location.pathname.includes('users/');
  const currentTab = routes.findIndex(route =>
    location.pathname.includes(route),
  );

  const { canCreateUser } = useUserManagementPermissions();
  const { openCreateOrgDrawer, openCreateUserDrawer } =
    useUserManagementContext();

  const isTeamsPage = currentTab === 1;
  const isUsersPage = currentTab === 0;

  const { idToken, profile } = useIdentity();
  const configApi = useApi(configApiRef);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const inviteApplications = useMemo((): InviteApplication[] => {
    const raw = configApi.getOptionalString(
      'userManagement.inviteApplications',
    );

    if (!raw) return [];
    try {
      const parsed: Array<Record<string, string>> = JSON.parse(raw);
      return parsed.flatMap(entry =>
        Object.entries(entry).map(([label, value]) => ({ label, value })),
      );
    } catch {
      return [];
    }
  }, [configApi]);

  const handleChange = (_: any, newValue: number) => {
    navigate(`${routes[newValue]}`);
  };

  const { data: user, isPending } = useUser(profile?.userId);
  const { setLoggedInUserData } = useUserManagementContext();

  useEffect(() => {
    setLoggedInUserData(user ?? null);
  }, [user, setLoggedInUserData]);

  const userHasReadPermission = user?.attributes.includes(USER_MANAGEMENT_READ);
  const userHasOrgAdminAccess = user?.attributes.includes(
    USER_MANAGEMENT_ORG_ADMIN_ACCESS,
  );

  const hasPermissions = userHasReadPermission || userHasOrgAdminAccess;

  if (isPending) {
    return <Progress />;
  }

  if (!idToken) {
    return (
      <Confirm
        isOpen
        title="You do not have enough permissions to access the application"
        content="Your current role does not allow access to the Admin Portal. Please contact your administrator if you believe this is an error."
        cancel="Dismiss"
        onClose={() => {
          navigate('/');
        }}
      />
    );
  }

  if (!hasPermissions) {
    return (
      <Confirm
        isOpen
        title="You do not have permissions to view this site"
        content="You don't have access to this page. Please log in with the correct account or contact an administrator."
        cancel="Close"
        onClose={() => {
          navigate('/');
        }}
      />
    );
  }

  return (
    <>
      {!isDetailsPage && (
        <Header
          pageTitleOverride="User Management"
          title={
            <FlexBox
              sx={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <PageTitle>User Management</PageTitle>
              <FlexBox sx={{ gridColumnGap: 8, justifyContent: 'flex-end' }}>
                {isTeamsPage && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      openCreateOrgDrawer();
                    }}
                    startIcon={<AddIcon />}
                  >
                    Create Team
                  </Button>
                )}

                {isUsersPage && canCreateUser && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      openCreateUserDrawer();
                    }}
                    startIcon={<AddIcon />}
                  >
                    Create User
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setIsInviteModalOpen(true)}
                >
                  Generate Magic Link
                </Button>
              </FlexBox>
            </FlexBox>
          }
        />
      )}
      {isDetailsPage && (
        <Box style={{ gridArea: 'pageHeader', padding: 24, paddingBottom: 0 }}>
          <Button
            variant="text"
            onClick={() => navigate('users')}
            startIcon={<ArrowBack />}
            color="primary"
          >
            Back
          </Button>
        </Box>
      )}
      <Content>
        {!isDetailsPage && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Tabs
              textColor="primary"
              indicatorColor="primary"
              value={currentTab}
              onChange={handleChange}
            >
              <StyledTab label="Users" />
              <StyledTab label="Teams" />
            </Tabs>
          </Box>
        )}
        <Box sx={{ mt: 4, display: 'flex', height: 'calc(100% - 80px)' }}>
          <Outlet />
        </Box>
      </Content>

      <EditUserDrawer />
      <CreateUserDrawer />
      <CreateOrgDrawer />
      <EditOrgDrawer />
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        applications={inviteApplications}
      />
    </>
  );
};

export default UserManagementLayout;
